/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import React, { useEffect, useState } from 'react';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { IBuyboxCallbacks, IBuyboxState, IBuyboxViewProps } from './farmlands-buybox';
import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps
} from './components';
import Quantity from '../../themes/farmlandstheme/utilities/quantity/Quantity';
import { IBuyboxProps } from '../../themes/farmlandstheme/definition-extensions/buybox.ext.props.autogenerated';
import { IFarmlandsBuyboxData } from './farmlands-buybox.data';
import { IFarmlandsCartData } from '../farmlands-cart/farmlands-cart.data';
import { IFarmlandsCartProps, IFarmlandsCartResources } from '../farmlands-cart/farmlands-cart.props.autogenerated';
import { getExtensionProperty } from '../../common/method/functions';
import { getDeliveryOptionsAsync } from '../../actions/farmlands-get-delivery-options-calls.action';
import { IBuyboxFindInStoreViewProps } from './components/buybox-find-in-store';


type DeviceType = 'Mobile';
interface IDeliveryMethods {
    delivery: boolean;
    clickCollect: boolean;
}

const BuyboxView: React.FC<IBuyboxViewProps & IFarmlandsCartProps<IFarmlandsCartData>> = (props) => {
    const {
        ModuleProps,
        MediaGalleryContainerProps,
        ProductInfoContainerProps,
        addToCart,
        addToWishlist,
        configure,
        findInStore,
        quantity,
        price,
        title,
        inventoryLabel,
        callbacks,
        state,
        resources,
        data,
        deliveryBlock,
        clickCollectBlock
    } = props;

    const shortDescProp = getExtensionProperty(data.product.result?.ExtensionProperties!, 'OnlineShortDescription');

    const isMobile = props.context.request &&
        props.context.request.device &&
        (props.context.request.device.Type as DeviceType) === 'Mobile';

    const [deliveryMethods, setDeliveryMethods] = useState<IDeliveryMethods | undefined>(undefined);

    useEffect(() => {
        const fetchData = async () => {
            const salesPriceInclGST = getExtensionProperty(
                props.data.productPrice.result?.ExtensionProperties!, 'ItemPriceWithGST'
                ).Value.DecimalValue;
            const recordId = data.product.result?.RecordId;

            const response = await getDeliveryOptionsAsync({
                context: { callerContext: props.context.actionContext, queryResultSettings: {} },
                recordId,
                salesPriceInclGST
            });

            const { DeliveryOptions } = response;

            setDeliveryMethods({
                delivery: hasDeliveryType(DeliveryOptions, 'Delivery'),
                clickCollect: hasDeliveryType(DeliveryOptions, 'Pickup')
            });
        }

        fetchData();
    }, [data.product.result?.RecordId]);

    return (
        <Module {...ModuleProps}>
            <Node {...MediaGalleryContainerProps}>{props.mediaGallery}</Node>
            <Node {...ProductInfoContainerProps}>
                {title}
                <div className='ms-buybox__sku'> SKU: {props.data.productPrice.result?.ItemId}</div>
                <input type="hidden" id="product-id" value={props.data.product.result?.RecordId} />
                {shortDescProp && <p className='ms-buybox__short-description'>{shortDescProp.Value.StringValue}</p>}
                <div className='ms-buybox__promo-wrapper'>{props.contentBlock}</div>
                {/* {rating} */}
                {price}
                {/* {description} */}
                <hr className='ms-buybox__divider' />
                <div className='ms-buybox__selection'>
                    {configure && _renderConfigure(configure)}
                    {quantity && _renderQuantity(quantity, callbacks, props, state, resources)}
                </div>
                {inventoryLabel}
                <div className='ms-buybox__addto'>
                    {addToCart && _renderAddToCart(addToCart)}
                    {addToWishlist && _renderAddToWishlist(addToWishlist)}
                </div>
                {
                    deliveryMethods !== undefined && (
                        <>
                            <hr className='ms-buybox__divider' />
                            <DeliveryMethods data={{
                                deliveryBlock,
                                clickCollectBlock,
                                deliveryMethods,
                                findInStore,
                                isMobile
                            }} />
                        </>
                    )
                }
            </Node>
        </Module>
    );
};

const DeliveryMethods = (props: any): JSX.Element => {
    const {
        deliveryBlock,
        clickCollectBlock,
        deliveryMethods,
        findInStore,
        isMobile
    } = props.data;

    return (
        <div className='ms-buybox__delivery-methods'>
            <h4 className='ms-buybox__delivery-methods__heading'>Delivery methods</h4>
            <div className='ms-buybox__delivery-methods__content'>
                <div className='item'>
                    {
                        deliveryBlock && (
                            <img
                                className='farmlands-promotion-block__image'
                                src={deliveryBlock.src}
                                alt={deliveryBlock.altText}
                            />
                        )
                    }
                    <p>Delivery {isMobile && <br/>}{!deliveryMethods.delivery  && 'un'}available</p>
                </div>
                <div className="item">
                    {
                        clickCollectBlock && (
                            <img
                                className='farmlands-promotion-block__image'
                                src={clickCollectBlock.src}
                                alt={clickCollectBlock.altText}
                            />
                        )
                    }
                    <p>Click &amp; Collect {isMobile && <br/>}{!deliveryMethods.clickCollect && 'un'}available</p>
                </div>
                {findInStore && deliveryMethods.clickCollect && _renderFindInStore(findInStore)}
            </div>
        </div>
    );
}

const hasDeliveryType = (deliveryOptions: any, type: string): boolean => {
    const options = deliveryOptions.filter((option: any) => option.DeliveryType === type);
    return options.length > 0;
}

const _renderAddToCart = (addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
    const { ContainerProps, button } = addToCart;

    return <Node {...ContainerProps}>{button}</Node>;
};

const _renderAddToWishlist = (addToWishlist: IBuyboxAddToWishlistViewProps): JSX.Element => {
    const { ContainerProps, button } = addToWishlist;

    return <Node {...ContainerProps}>{button}</Node>;
};

const _renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(_renderConfigureDropdown)}</Node>;
};

const _renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select, sizeGuideLink } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {select}
            {errors}
            {sizeGuideLink}
        </Node>
    );
};

const _renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    const {
        ContainerProps,
        storeSelector,
        heading,
        description,
        button,
        modal,
        productPickupOptionList
    } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {productPickupOptionList}
            {description}
            {button}
            {modal}
        </Node>
    );
};

const _renderQuantity = (
    quantityComponent: IBuyboxProductQuantityViewProps,
    callbacks: IBuyboxCallbacks,
    props: IBuyboxProps<IFarmlandsBuyboxData> | IFarmlandsCartProps<IFarmlandsCartData>,
    state: IBuyboxState,
    extentionResources: IFarmlandsCartResources
): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;

    const {
        resources,
        context: {
            app: {
                config: { maxQuantityForCartLineItem }
            }
        }
    } = props;

    const { quantity } = state;

    const onChange = (newValue: number) => {
        if (callbacks.updateQuantity) {
            callbacks.updateQuantity(newValue);
        }
    };

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>

            <Quantity
                id='ms-buybox__product-quantity-input'
                max={maxQuantityForCartLineItem || 10}
                currentCount={quantity}
                onChange={onChange}
                inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                decrementButtonAriaLabel={extentionResources.decrementButtonAriaLabel}
                incrementButtonAriaLabel={extentionResources.incrementButtonAriaLabel}
            />
        </Node>
    );
};

export default BuyboxView;
